import { supabase } from "./supabase";
import { ItemImage } from "./types";
import type { Database, Json } from "./database.types";

export {
  buildListingImageSignature,
  computeListingImageContentHash,
  computeListingImageContentHashFromBlob,
  evaluateListingPhotoUniqueness,
} from "@/lib/listing/listingPhotoDuplicateCheck";

// Type for database item_images row
type ItemImageRow = Database["public"]["Tables"]["item_images"]["Row"];

/**
 * Helper function to check if a value is a record (object but not array)
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Converts JSON dimensions to typed dimensions object
 */
function toImageDimensions(
  value: Json | null,
): { width: number; height: number } | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const width = typeof record.width === "number" ? record.width : undefined;
  const height = typeof record.height === "number" ? record.height : undefined;

  if (typeof width === "number" && typeof height === "number") {
    return { width, height };
  }

  return undefined;
}

/**
 * Converts JSON metadata to typed metadata object
 */
function toImageMetadata(
  value: Json | null,
): Record<string, unknown> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

/**
 * Maps a Supabase item_images row to the ItemImage domain type.
 *
 * Handles:
 * - snake_case → camelCase conversion
 * - null handling with defaults
 * - Date string → Date object conversion
 * - JSON field validation and conversion
 *
 * @param row - The database row from Supabase
 * @returns A properly typed ItemImage domain object
 */
function mapRowToItemImage(row: ItemImageRow): ItemImage {
  return {
    id: row.id,
    itemId: row.item_id,
    url: row.url,
    position: row.position ?? 0,
    caption: row.caption ?? undefined,
    dimensions: toImageDimensions(row.dimensions),
    fileSize: row.file_size ?? undefined,
    fileType: row.file_type ?? undefined,
    isMain: Boolean(row.is_main),
    metadata: toImageMetadata(row.metadata),
    createdAt: row.created_at ? new Date(row.created_at) : undefined,
    hash: row.image_hash ?? undefined,
    signature: row.image_signature ?? undefined,
  };
}

/**
 * Gets all images for an item
 *
 * @param itemId - The ID of the item
 * @returns Array of ItemImage domain objects
 */
export async function getItemImages(itemId: string): Promise<ItemImage[]> {
  try {
    const { data, error } = await supabase
      .from("item_images")
      .select("*")
      .eq("item_id", itemId)
      .order("position", { ascending: true });

    if (error) {
      console.error("Error fetching item images:", error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Map database rows to domain types
    return data.map(mapRowToItemImage);
  } catch (error) {
    console.error("Error in getItemImages:", error);
    return [];
  }
}

/**
 * Gets images for multiple items in a single query, grouped by item ID.
 */
export async function getItemImagesByItemIds(
  itemIds: string[],
): Promise<Map<string, ItemImage[]>> {
  const imagesByItemId = new Map<string, ItemImage[]>();
  const uniqueItemIds = [...new Set(itemIds)].filter(Boolean);

  if (uniqueItemIds.length === 0) {
    return imagesByItemId;
  }

  try {
    const { data, error } = await supabase
      .from("item_images")
      .select("*")
      .in("item_id", uniqueItemIds)
      .order("item_id", { ascending: true })
      .order("position", { ascending: true });

    if (error) {
      console.error("Error fetching item images by item IDs:", error);
      return imagesByItemId;
    }

    for (const row of data ?? []) {
      const image = mapRowToItemImage(row);
      const existingImages = imagesByItemId.get(image.itemId) ?? [];
      existingImages.push(image);
      imagesByItemId.set(image.itemId, existingImages);
    }

    return imagesByItemId;
  } catch (error) {
    console.error("Error in getItemImagesByItemIds:", error);
    return imagesByItemId;
  }
}
